import React, { useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'
import { Stack } from 'react-bootstrap'
import type { PopoverProps } from 'react-bootstrap'
import { useToggle } from 'react-use'
import type { ReactNode, RefObject } from 'react'
import type { PivotDateRange, PivotLayoutField, PivotLayoutFilter } from '@ztechapps/common.contract'
import type { PivotData, PivotMember } from '../pivot/types'
import type { DateType } from '@ztechapps/common.utils'
import { DirectionEnum, PivotFieldTypeEnum } from '@ztechapps/common.contract'
import { getWindow } from '@ztechapps/common.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { BasePopover } from '@ztechapps/webui.data-tools'
import { DateTimeInput } from '@ztechapps/webui.date'
import { CheckboxInput, TextInput } from '@ztechapps/webui.input'
import { CloseButton, HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { createPivotMemberMatcher } from '../pivot/createPivotMemberMatcher'
import { listPivotMembers } from '../pivot/listPivotMembers'
import { usePivotViewerContext } from '../PivotViewer/Context'
import styles from './PivotFilterPopover.module.scss'

// BasePopover's body is 350px wide — this covers the body plus the arrow gap, so the
// flip check keeps the whole popover inside the viewport instead of kissing its edge.
const POPOVER_WIDTH = 370

/** What a custom trigger receives — the ref the popover anchors to, the click that opens it and whether it is open. */
export type PivotFilterPopoverTriggerProps = {
  ref: RefObject<HTMLButtonElement | null>
  onClick: () => void
  'aria-expanded': boolean
}

/** Props for the pivot filter popover. */
export type PivotFilterPopoverProps = {
  data: PivotData
  field: PivotLayoutField
  filter?: PivotLayoutFilter
  timeZone: string
  onSave: (excludedValues: string[], dateRange?: PivotDateRange) => void
  sortDirection?: DirectionEnum
  onSort?: (direction?: DirectionEnum) => void
  trigger?: (props: PivotFilterPopoverTriggerProps) => ReactNode
}

// A date input reports `YYYY-MM-DDTHH:mm`, or '' once cleared; the range keeps the day alone.
const toDay = (value: DateType): string | undefined => String(value ?? '').slice(0, 10) || undefined

/** Filter button opening a searchable member picker for a pivot field. */
export const PivotFilterPopover = ({
  data,
  field,
  filter,
  timeZone,
  onSave,
  sortDirection,
  onSort,
  trigger,
}: PivotFilterPopoverProps) => {
  const [show, setShow] = useToggle(false)
  const [members, setMembers] = useState<PivotMember[]>([])
  const [placement, setPlacement] = useState<PopoverProps['placement']>('right')
  const [unchecked, setUnchecked] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [rangeFrom, setRangeFrom] = useState<string | undefined>(undefined)
  const [rangeTo, setRangeTo] = useState<string | undefined>(undefined)
  // The popover anchors to the trigger button's element, not the wrapper span, so it
  // positions against the clicked gear — the same ref-anchored split MenuHeaderCell uses
  // for its trigger and table-config popover.
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { container } = usePivotViewerContext()

  const isActive =
    !isEmpty(filter?.excludedValues) || !isEmpty(filter?.includedValues) || filter?.dateRange !== undefined
  // a day range edits the local days of an ungrouped date field only — grouped parts keep the member checklist alone
  const showRange =
    data.fields.find((item) => item.name === field.field)?.type === PivotFieldTypeEnum.Date &&
    field.dateGroup === undefined
  const open = () => {
    // BasePopover renders a fixed 350px-wide body with no flip, so a trigger near the
    // right edge would paint the popover off-viewport — open to the left instead.
    const rect = triggerRef.current?.getBoundingClientRect()
    setPlacement(rect && rect.right + POPOVER_WIDTH > getWindow().innerWidth ? 'left' : 'right')
    const next = listPivotMembers({ data, field, timeZone })
    setMembers(next)
    const matches = createPivotMemberMatcher({
      includedValues: filter?.includedValues,
      excludedValues: filter?.excludedValues,
    })
    setUnchecked(new Set(next.filter((member) => !matches(member.key)).map((member) => member.key)))
    setSearch('')
    setRangeFrom(filter?.dateRange?.from)
    setRangeTo(filter?.dateRange?.to)
    setShow(true)
  }

  const visible = members.filter((member) => toLower(member.label).includes(toLower(search)))
  const visibleKeys = new Set(map(visible, 'key'))

  return (
    <span>
      {trigger ? (
        trigger({ ref: triggerRef, onClick: open, 'aria-expanded': show })
      ) : onSort ? (
        <>
          <ShowIf condition={isActive}>
            <BusyButton
              variant="link"
              bsSize="sm"
              className={`p-0 ${styles.filterButton}`}
              icon="filter"
              active
              aria-label={`Filter ${field.field}`}
              aria-expanded={show}
              onClick={open}
            />
          </ShowIf>
          <BusyButton
            variant="link"
            bsSize="sm"
            className={`p-0 ${styles.filterButton}`}
            icon="gear"
            aria-label={`Open ${field.field} settings`}
            aria-expanded={show}
            ref={triggerRef}
            onClick={open}
          />
        </>
      ) : (
        <BusyButton
          variant="link"
          bsSize="sm"
          className={`p-0 ${styles.filterButton}`}
          icon="filter"
          aria-label={`Filter ${field.field}`}
          aria-expanded={show}
          active={isActive}
          ref={triggerRef}
          onClick={open}
        />
      )}
      <BasePopover
        name="pivot-filter"
        show={show}
        setShow={setShow}
        target={triggerRef.current}
        placement={placement}
        focusRef={searchRef}
        container={container}
        header={
          <HorizontalStack className="justify-content-between">
            <span>Filter: {field.field}</span>
            <CloseButton onClick={() => setShow(false)} />
          </HorizontalStack>
        }
      >
        <Stack gap={2}>
          <ShowIf condition={!!onSort}>
            <div role="group" aria-label={`Sort ${field.field}`} className="btn-group btn-group-sm">
              <BusyButton
                variant="outline-secondary"
                icon="sort-alpha-asc"
                text="A to Z"
                active={sortDirection === DirectionEnum.asc}
                onClick={() => onSort?.(sortDirection === DirectionEnum.asc ? undefined : DirectionEnum.asc)}
              />
              <BusyButton
                variant="outline-secondary"
                icon="sort-alpha-desc"
                text="Z to A"
                active={sortDirection === DirectionEnum.desc}
                onClick={() => onSort?.(sortDirection === DirectionEnum.desc ? undefined : DirectionEnum.desc)}
              />
            </div>
          </ShowIf>
          <TextInput
            inputRef={searchRef}
            placeholder="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <ShowIf condition={showRange}>
            <div>
              <label htmlFor={`pivot-date-range-from-${field.field}`} className="small fw-semibold">
                From
              </label>
              <DateTimeInput
                id={`pivot-date-range-from-${field.field}`}
                type="date"
                value={rangeFrom ?? ''}
                max={rangeTo}
                onChange={(value) => setRangeFrom(toDay(value))}
              />
            </div>
            <div>
              <label htmlFor={`pivot-date-range-to-${field.field}`} className="small fw-semibold">
                To
              </label>
              <DateTimeInput
                id={`pivot-date-range-to-${field.field}`}
                type="date"
                value={rangeTo ?? ''}
                min={rangeFrom}
                onChange={(value) => setRangeTo(toDay(value))}
              />
            </div>
            <div className="small text-body-secondary">Rows with no date are left out of the range.</div>
          </ShowIf>
          <HorizontalStack gap={2}>
            <BusyButton
              text="Select all"
              onClick={() => setUnchecked(new Set([...unchecked].filter((key) => !visibleKeys.has(key))))}
            />
            <BusyButton text="Clear members" onClick={() => setUnchecked(new Set([...unchecked, ...visibleKeys]))} />
          </HorizontalStack>
          {/* capped independently of BasePopover's own 80vh body scroll, so Clear filter/OK below stay
              reachable without scrolling past every member first. */}
          <Stack gap={2} className="overflow-y-auto" style={{ maxHeight: '260px' }}>
            {map(visible, (member) => (
              <CheckboxInput
                key={member.key}
                // The capped member list is a flex column, and Bootstrap pins .form-check
                // to an explicit min-height — together the rows shrink to one line and
                // wrapped labels paint over each other, so each row opts out of shrinking.
                className="flex-shrink-0"
                label={member.label}
                checked={!unchecked.has(member.key)}
                onChange={(set: boolean) => {
                  const next = new Set(unchecked)
                  if (set) next.delete(member.key)
                  else next.add(member.key)
                  setUnchecked(next)
                }}
              />
            ))}
          </Stack>
          <HorizontalStack className="justify-content-between border-top pt-3">
            <BusyButton
              text="Clear filter"
              bsStyle="warning"
              onClick={() => {
                onSave([], undefined)
                setShow(false)
              }}
            />
            <BusyButton
              variant="primary"
              text="OK"
              onClick={() => {
                onSave(
                  [...unchecked],
                  rangeFrom !== undefined && rangeTo !== undefined ? { from: rangeFrom, to: rangeTo } : undefined
                )
                setShow(false)
              }}
            />
          </HorizontalStack>
        </Stack>
      </BasePopover>
    </span>
  )
}
